/**
 * Gmail-style visual treatment for email recipient inputs (To / Cc / Bcc /
 * Forward). Drop-in `styles` prop and `formatOptionLabel` renderer for the
 * existing `CreatableSelect` / `ReactCreatableSelectWrapper` components.
 *
 * Chips render as name-only green brand pills; dropdown options render as an
 * avatar circle + name/email row.
 */

import { colors } from 'styles/tokens'
import { parseEmailOption } from './helpers'

export { RecipientRow, CcBccToggles } from './RecipientRow'
export {
  buildReplyAllCcOptions,
  filterSelf,
  looksLikeEmail,
  toWireRecipients,
} from './helpers'
export type { ComposeMode, ReplyMode, WireRecipient } from './helpers'

export interface EmailRecipientOption {
  label: string
  value: string
}

export const emailRecipientStyles = {
  // 12px field text across control, typed input, single value, and placeholder.
  // Transparent control so the recipient row's own (light gray) background
  // shows through uniformly instead of a white input box sitting on it.
  control: (provided: Record<string, unknown>) => ({
    ...provided,
    fontSize: '12px',
    backgroundColor: 'transparent',
    // Text cursor so hovering the control signals it's editable. (Row height is
    // driven by the `height` prop, since the wrapper overrides minHeight here.)
    cursor: 'text',
  }),
  // Tighten the value container so From/To rows are as compact as the Subject
  // input (react-select's default 2px/8px padding makes them taller).
  valueContainer: (provided: Record<string, unknown>) => ({
    ...provided,
    padding: '0 6px',
  }),
  input: (provided: Record<string, unknown>) => ({
    ...provided,
    fontSize: '12px',
    margin: 0,
    padding: 0,
  }),
  singleValue: (provided: Record<string, unknown>) => ({
    ...provided,
    fontSize: '12px',
    color: colors['neutral-800'],
  }),
  placeholder: (provided: Record<string, unknown>) => ({
    ...provided,
    fontSize: '12px',
  }),
  // DS2026 chip: neutral-100 gray pill with neutral text + Phosphor remove
  // (the remove glyph is swapped to PiX by the ds2026 wrapper).
  multiValue: (provided: Record<string, unknown>) => ({
    ...provided,
    backgroundColor: colors['neutral-100'],
    borderRadius: '6px',
    paddingLeft: '6px',
    paddingRight: '2px',
    margin: '2px 4px 2px 0',
    // Cap chip width so very long emails truncate to an ellipsis (the
    // `title` attr on chips still shows the full value on hover).
    maxWidth: '240px',
  }),
  multiValueLabel: (provided: Record<string, unknown>) => ({
    ...provided,
    color: colors['neutral-700'],
    fontSize: '12px',
    fontWeight: 500,
    padding: '2px 2px 2px 0',
    paddingLeft: 0,
    // react-select's default already has overflow/ellipsis; explicit for clarity.
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  }),
  multiValueRemove: (provided: Record<string, unknown>) => ({
    ...provided,
    color: colors['neutral-500'],
    borderRadius: '0px 6px 6px 0px',
    padding: '0 4px',
    marginLeft: '2px',
    ':hover': {
      backgroundColor: colors['neutral-200'],
      color: colors['neutral-700'],
    },
  }),
  // DS2026 gray menu (matches the new modal's react-select look): neutral-100
  // hover/focus on a rounded, inset option row rather than the green sage tint.
  menuList: (provided: Record<string, unknown>) => ({
    ...provided,
    padding: 6,
  }),
  option: (
    provided: Record<string, unknown>,
    state: { isFocused: boolean; isSelected: boolean }
  ) => ({
    ...provided,
    backgroundColor:
      state.isFocused || state.isSelected
        ? colors['neutral-100']
        : colors.surface,
    color: colors['neutral-800'],
    cursor: 'pointer',
    padding: '8px 10px',
    borderRadius: 8,
  }),
}

export function formatEmailRecipientOption(
  option: EmailRecipientOption,
  { context }: { context: 'menu' | 'value' }
) {
  const { name, email } = parseEmailOption(option)

  if (context === 'value') return name || email

  const initial = (name || email)[0]?.toUpperCase() || '?'

  return (
    <div className="flex items-center gap-3 w-full">
      <div className="size-8 rounded-full bg-neutral-100 flex items-center justify-center flex-shrink-0">
        <span className="text-xs font-medium text-neutral-600">{initial}</span>
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm text-neutral-800 truncate font-medium">
          {name || email}
        </div>
        {name && (
          <div className="text-xs text-neutral-500 truncate">{email}</div>
        )}
      </div>
    </div>
  )
}
